import {
  Scene,
  ArcRotateCamera,
  Vector3,
  Color4,
  Engine,
  BaseTexture,
  CubeTexture,
  Mesh,
  EquiRectangularCubeTexture,
  Texture,
  BackgroundMaterial,
} from 'babylonjs';
import { ContentInstanceManager } from '../../content/ContentInstanceManager';
import { CameraGestureController } from '../CameraGesture';
import { PreviewRenderer } from './PreviewRenderer';
import { FullscreenController, createFullscreenState } from './FullscreenController';
import type { IAssetModel } from '@webcore/core/loader/loader';
import type {
  PreviewView,
  PreviewManagerOptions,
  AttachCanvasConfig,
  EnvironmentConfig,
  ViewerEvent,
  ViewerEventHandler,
  FullscreenEventHandler,
} from './types';

// ============================================================================
// PREVIEW MANAGER
// ============================================================================
// Main class for managing 3D model previews.
//
// Usage:
//   const preview = new PreviewManager(engine, scene, instanceManager, options);
//   preview.attach(canvas, { contentId: 'model-1' });
//   preview.detach(canvas);
//   preview.dispose();

const DEFAULT_BACKGROUND = new Color4(0.1, 0.1, 0.12, 1);
const SKYBOX_BLUR = 0.5;
const LAZY_DEBOUNCE_MS = 300;
const SKYBOX_EQUIRECT_SIZE = 512;

/**
 * Main manager class for preview canvases
 */
export class PreviewManager {
  private engine: Engine;
  private scene: Scene;
  private instanceManager: ContentInstanceManager;
  private views: Map<HTMLCanvasElement, PreviewView> = new Map();
  private registeredConfigs: Map<HTMLCanvasElement, AttachCanvasConfig> = new Map();
  private environmentCache: Map<string, BaseTexture> = new Map();
  private environmentLoadingCallbacks: Map<string, ((texture: BaseTexture) => void)[]> = new Map();
  private skyboxTextureCache: Map<string, BaseTexture> = new Map();
  private skyboxLoadingCallbacks: Map<string, ((texture: BaseTexture) => void)[]> = new Map();
  
  private renderer: PreviewRenderer;
  private fullscreenController: FullscreenController;
  
  // Options
  private fullscreenEnabled: boolean;
  private rootMargin: string;
  private unloadOnDetach: boolean;
  private onFullscreenChange: FullscreenEventHandler | null;
  private onViewerEvent: ((canvas: HTMLCanvasElement, event: ViewerEvent) => void) | null;
  private loadingMode: 'lazy' | 'eager' | 'manual' = 'manual';
  
  // DOM observers for lazy/eager loading
  private intersectionObserver: IntersectionObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  // Debounce timeouts for lazy attach/detach (avoid rapid toggling on scroll)
  private lazyAttachTimeouts: Map<HTMLCanvasElement, number> = new Map();
  private lazyDetachTimeouts: Map<HTMLCanvasElement, number> = new Map();

  constructor(
    engine: Engine,
    scene: Scene,
    instanceManager: ContentInstanceManager,
    options: PreviewManagerOptions = {}
  ) {
    this.engine = engine;
    this.scene = scene;
    this.instanceManager = instanceManager;
    
    this.fullscreenEnabled = options.fullscreen ?? true;
    this.rootMargin = options.rootMargin ?? '100px';
    this.unloadOnDetach = options.unloadOnDetach ?? false;
    this.onFullscreenChange = options.onFullscreenChange ?? null;
    this.onViewerEvent = options.onViewerEvent ?? null;
    
    this.renderer = new PreviewRenderer(instanceManager, this.views);
    this.fullscreenController = new FullscreenController();
    
    // Setup collapse request handler
    this.fullscreenController.setCollapseRequestHandler((canvas) => {
      this.collapseFromFullscreen(canvas);
    });
    
    // Set loading mode - controls behavior of add()
    // true = lazy (observe visibility, load when in view)
    // false = eager (load immediately when added)
    if (options.lazyLoad === true) {
      this.loadingMode = 'lazy';
    } else if (options.lazyLoad === false) {
      this.loadingMode = 'eager';
    }
    // If undefined, defaults to 'lazy'
    if (options.lazyLoad === undefined) {
      this.loadingMode = 'lazy';
    }
  }

  // ===========================================================================
  // Public API
  // ===========================================================================

  /**
   * Attach a canvas to display a preview
   */
  attach(canvas: HTMLCanvasElement, config: AttachCanvasConfig): void {
    if (this.views.has(canvas)) {
      this.detach(canvas);
    }

    // Emit loading event
    this.emitViewerEvent(canvas, { type: 'loading' }, config.onEvent);

    // Create camera
    const camera = new ArcRotateCamera(
      `previewCamera_${config.contentId}`,
      Math.PI / 2,
      Math.PI / 2,
      5,
      Vector3.Zero(),
      this.scene
    );
    camera.minZ = 0.01;
    camera.maxZ = 1000;
    camera.wheelPrecision = 50;
    camera.panningSensibility = 0;
    camera.attachControl(false);

    // Register view with engine
    const view = this.engine.registerView(canvas, camera);

    // Create gesture controller with settings from config
    const rotation = config.rotation ?? 'horizontal';
    const zoom = config.zoom ?? false;
    const allowPageScroll = config.allowPageScroll ?? true;
    const gestureController = new CameraGestureController({
      minZoom: 0.5,
      maxZoom: 2,
      rotationSpeed: 0.005,
      rotation,
      zoom,
      allowPageScroll,
    });

    // Create preview view
    const preview: PreviewView = {
      canvas,
      camera,
      view,
      contentId: config.contentId,
      environmentPath: config.environmentPath,
      iblEnvironmentPath: config.iblEnvironmentPath,
      backgroundColor: config.backgroundColor ?? DEFAULT_BACKGROUND,
      rotationY: config.rotationY ?? 0,
      viewerOffset: config.viewerOffset ?? { x: 0, y: 0 },
      baseRadius: 5,
      isFramed: false,
      isReady: false,
      animationsStarted: false,
      lastFramedContentId: null,
      eventHandler: config.onEvent ?? null,
      environmentTexture: null,
      iblEnvironmentTexture: null,
      skybox: null,
      excludeMeshes: config.excludeMeshes ?? null,
      includeMeshes: config.includeMeshes ?? null,
      gestureController,
      rotation,
      zoom,
      allowPageScroll,
      fullscreen: createFullscreenState(),
      loadingOverlay: null,
      expandButton: null,
      expandTooltip: null,
      expandTooltipDismissed: false,
      fullscreenUI: null,
      fullscreenOverlay: null,
    };

    // Show loading overlay
    preview.loadingOverlay = this.fullscreenController.createLoadingOverlay(canvas);

    this.views.set(canvas, preview);

    // Auto-attach gestures when any interaction is enabled (rotation or zoom)
    const hasInteraction = rotation !== 'none' || zoom;
    if (hasInteraction) {
      gestureController.attach(canvas);
    }

    // Load environment if specified
    if (config.environmentPath) {
      this.loadEnvironment(preview, config.contentId, config.environmentPath);
    }

    if (config.iblEnvironmentPath) {
      this.loadIblEnvironment(preview, config.contentId, config.iblEnvironmentPath);
    }

    // Load content
    this.loadContent(preview);
  }

  /**
   * Detach a canvas from preview
   */
  detach(canvas: HTMLCanvasElement): void {
    const preview = this.views.get(canvas);
    if (!preview) return;

    // Dispose gesture controller
    preview.gestureController.detach();

    // Dispose camera
    preview.camera.dispose();

    // Unregister view
    this.engine.unRegisterView(canvas);

    // Dispose skybox
    if (preview.skybox) {
      preview.skybox.dispose();
    }

    // Dispose UI elements
    this.fullscreenController.disposeUI(preview);
    this.fullscreenController.removeEscapeKeyListener(canvas);

    // Unload model instance if enabled (frees GPU memory)
    // Skip if this is the current active instance (e.g., selected for AR)
    if (this.unloadOnDetach) {
      const isCurrentInstance = this.instanceManager.currentInstance?.contentItem?.id === preview.contentId;
      if (!isCurrentInstance) {
        this.instanceManager.disposeInstanceById(preview.contentId);
      }
    }

    this.views.delete(canvas);
  }

  /**
   * Stop all observation without disposing content instances.
   * Use this before XR to preserve the selected model while stopping preview updates.
   */
  stop(): void {
    this.stopLazyLoading();
    this.stopEagerLoading();
  }

  /**
   * Dispose all previews and cleanup
   */
  dispose(): void {
    // Stop observers
    this.stopLazyLoading();
    this.stopEagerLoading();

    // Clear scene env so we don't hold a ref to a texture we're about to dispose
    this.scene.environmentTexture = null;

    // Detach all views (snapshot keys - detach modifies this.views during iteration)
    for (const canvas of Array.from(this.views.keys())) {
      this.detach(canvas);
    }
    // Safety reset: preview filters may have hidden meshes; restore all for non-preview flows (e.g. AR).
    this.resetAllInstanceMeshesVisible();

    // Dispose environment cache
    for (const texture of this.environmentCache.values()) {
      texture.dispose();
    }
    this.environmentCache.clear();
    this.environmentLoadingCallbacks.clear();

    for (const texture of this.skyboxTextureCache.values()) {
      texture.dispose();
    }
    this.skyboxTextureCache.clear();
    this.skyboxLoadingCallbacks.clear();
  }

  /**
   * Restart observation after dispose (e.g., after XR exit)
   * Uses the same loading mode that was configured initially
   * Re-observes/re-attaches canvases from registeredConfigs
   */
  restart(): void {
    if (this.loadingMode === 'lazy') {
      this.startLazyLoading();
      // Re-observe all registered canvases and immediately attach visible ones
      for (const [canvas, config] of this.registeredConfigs) {
        this.intersectionObserver?.observe(canvas);
        // Check if canvas is currently visible and attach immediately
        const rect = canvas.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        if (isVisible && !this.views.has(canvas)) {
          this.attach(canvas, config);
        }
      }
    } else if (this.loadingMode === 'eager') {
      this.startEagerLoading();
      // Re-attach all registered canvases
      for (const [canvas, config] of this.registeredConfigs) {
        this.attach(canvas, config);
      }
    }
    // 'manual' mode does nothing - requires explicit attach() calls
  }

  /**
   * Add a canvas for preview with its config.
   * If lazyLoad is true, observes visibility and loads when in view.
   * If lazyLoad is false, loads immediately.
   */
  add(canvas: HTMLCanvasElement, config: AttachCanvasConfig): void {
    // Store config for this canvas
    this.registeredConfigs.set(canvas, config);
    
    if (this.loadingMode === 'lazy') {
      // Lazy: observe visibility, attach when in view (with debounce)
      if (!this.intersectionObserver) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              this.handleLazyIntersection(entry);
            }
          },
          { rootMargin: this.rootMargin }
        );
      }
      this.intersectionObserver.observe(canvas);
    } else {
      // Eager: attach immediately
      this.attach(canvas, config);
    }
  }

  /**
   * Remove a canvas from preview.
   */
  remove(canvas: HTMLCanvasElement): void {
    // Remove stored config
    this.registeredConfigs.delete(canvas);
    
    if (this.intersectionObserver) {
      this.intersectionObserver.unobserve(canvas);
    }
    // Also detach if currently attached
    if (this.views.has(canvas)) {
      this.detach(canvas);
    }
  }

  /**
   * Create render callback for multi-view rendering.
   *
   * @param mainRenderFn - Function to call for main canvas rendering
   * @param shouldRenderMain - Optional callback to determine if main should render (default: when no previews active)
   */
  createRenderCallback(
    mainRenderFn: () => void,
    shouldRenderMain?: () => boolean
  ): () => void {
    return () => {
      const activeView = this.engine.activeView;

      // Check if active view is a preview canvas
      if (activeView?.target) {
        const targetCanvas = activeView.target as HTMLCanvasElement;
        const preview = this.views.get(targetCanvas);

        if (preview?.view.enabled) {
          const wasReady = preview.isReady;
          
          this.renderer.renderPreview(targetCanvas, this.scene);

          // Fade out tooltip only after actual rotation movement (not just touch/click down).
          if (
            !preview.expandTooltipDismissed &&
            preview.gestureController.consumeRotationGesture()
          ) {
            preview.expandTooltipDismissed = true;
            this.fullscreenController.hideExpandTooltip(preview);
          }
          
          // Check if this preview just became ready (first successful render)
          if (preview.isReady && !wasReady) {
            // Hide loading overlay
            this.fullscreenController.hideLoadingOverlay(preview);
            
            // Emit loaded event now that content is actually visible
            this.emitViewerEvent(preview.canvas, { type: 'loaded' }, preview.eventHandler);
            
            // Setup expand button if fullscreen is enabled
            if (this.fullscreenEnabled) {
              this.setupExpandButton(preview);
            }
          }
          return;
        }
      }

      // Determine if we should render main
      const shouldRender = shouldRenderMain 
        ? shouldRenderMain() 
        : !this.hasActivePreview();

      if (shouldRender) {
        mainRenderFn();
      }
    };
  }

  // ===========================================================================
  // Fullscreen API
  // ===========================================================================

  /**
   * Expand a canvas to fullscreen
   */
  expandToFullscreen(canvas: HTMLCanvasElement): void {
    if (!this.fullscreenEnabled) return;

    const preview = this.views.get(canvas);
    if (!preview) return;

    // Fullscreen enables full orbit+zoom controls
    preview.gestureController.setOptions({
      rotation: 'both',
      zoom: true,
      allowPageScroll: false,
    });

    // Enable interactions when entering fullscreen
    if (!preview.gestureController.attached) {
      preview.gestureController.attach(canvas);
    }

    this.fullscreenController.expand(canvas, preview, this.engine, () => {
      this.emitFullscreenEvent(canvas, true);
    });
  }

  /**
   * Collapse from fullscreen
   */
  collapseFromFullscreen(canvas: HTMLCanvasElement): void {
    const preview = this.views.get(canvas);
    if (!preview) return;

    // Emit event immediately for UI to respond
    this.emitFullscreenEvent(canvas, false);

    this.fullscreenController.collapse(canvas, preview, this.engine, () => {
      // Restore inline interaction constraints after exiting fullscreen
      preview.gestureController.setOptions({
        rotation: preview.rotation,
        zoom: preview.zoom,
        allowPageScroll: preview.allowPageScroll,
      });

      // Check if gestures should remain attached based on config
      const hasInteraction = preview.rotation !== 'none' || preview.zoom;

      if (!hasInteraction && preview.gestureController.attached) {
        preview.gestureController.detach();
      }
    });
  }

  /**
   * Toggle fullscreen for a canvas
   */
  toggleFullscreen(canvas: HTMLCanvasElement): void {
    const preview = this.views.get(canvas);
    if (!preview) return;

    if (preview.fullscreen.isFullscreen) {
      this.collapseFromFullscreen(canvas);
    } else {
      this.expandToFullscreen(canvas);
    }
  }

  /**
   * Check if a canvas is in fullscreen
   */
  isFullscreen(canvas: HTMLCanvasElement): boolean {
    const preview = this.views.get(canvas);
    return preview ? this.fullscreenController.isFullscreen(preview) : false;
  }

  // ===========================================================================
  // Interactions API
  // ===========================================================================

  /**
   * Enable or disable interactions for a canvas
   */
  setInteractionsEnabled(canvas: HTMLCanvasElement, enabled: boolean): void {
    const preview = this.views.get(canvas);
    if (!preview) return;

    if (enabled && !preview.gestureController.attached) {
      preview.gestureController.attach(canvas);
    } else if (!enabled && preview.gestureController.attached) {
      preview.gestureController.detach();
    }
  }

  /**
   * Check if interactions are enabled for a canvas
   */
  isInteractionsEnabled(canvas: HTMLCanvasElement): boolean {
    const preview = this.views.get(canvas);
    return preview?.gestureController.attached ?? false;
  }

  // ===========================================================================
  // Loading Modes
  // ===========================================================================

  /**
   * Start lazy loading - attach canvases when they scroll into view
   */
  startLazyLoading(): void {
    if (this.intersectionObserver) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.handleLazyIntersection(entry);
        }
      },
      { rootMargin: this.rootMargin }
    );

    // Observe all existing preview canvases
    const canvases = document.querySelectorAll<HTMLCanvasElement>('[data-preview-content]');
    canvases.forEach((canvas) => {
      this.intersectionObserver!.observe(canvas);
    });

    // Watch for new canvases added to the DOM (e.g., by React rendering)
    if (!this.mutationObserver) {
      this.mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          // Check added nodes for preview canvases
          for (const node of mutation.addedNodes) {
            if (node instanceof HTMLCanvasElement && node.hasAttribute('data-preview-content')) {
              this.intersectionObserver?.observe(node);
            }
            // Also check children of added nodes
            if (node instanceof HTMLElement) {
              const canvases = node.querySelectorAll<HTMLCanvasElement>('[data-preview-content]');
              canvases.forEach((canvas) => {
                this.intersectionObserver?.observe(canvas);
              });
            }
          }
          // Handle removed nodes
          for (const node of mutation.removedNodes) {
            if (node instanceof HTMLCanvasElement && this.views.has(node)) {
              this.detach(node);
            }
          }
        }
      });

      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Stop lazy loading
   */
  stopLazyLoading(): void {
    // Clear any pending debounced attach/detach
    for (const id of this.lazyAttachTimeouts.values()) clearTimeout(id);
    for (const id of this.lazyDetachTimeouts.values()) clearTimeout(id);
    this.lazyAttachTimeouts.clear();
    this.lazyDetachTimeouts.clear();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  /**
   * Start eager loading - attach all canvases immediately
   */
  startEagerLoading(): void {
    // Attach all existing canvases
    const canvases = document.querySelectorAll<HTMLCanvasElement>('[data-preview-content]');
    canvases.forEach((canvas) => {
      const config = this.parseCanvasConfig(canvas);
      if (config) {
        this.attach(canvas, config);
      }
    });

    // Watch for new canvases
    if (!this.mutationObserver) {
      this.mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node instanceof HTMLCanvasElement && node.hasAttribute('data-preview-content')) {
              const config = this.parseCanvasConfig(node);
              if (config) {
                this.attach(node, config);
              }
            }
            // Also check children of added nodes
            if (node instanceof HTMLElement) {
              const canvases = node.querySelectorAll<HTMLCanvasElement>('[data-preview-content]');
              canvases.forEach((canvas) => {
                if (!this.views.has(canvas)) {
                  const config = this.parseCanvasConfig(canvas);
                  if (config) {
                    this.attach(canvas, config);
                  }
                }
              });
            }
          }
          for (const node of mutation.removedNodes) {
            if (node instanceof HTMLCanvasElement) {
              this.detach(node);
            }
          }
        }
      });

      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Stop eager loading
   */
  stopEagerLoading(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  // ===========================================================================
  // Environment Preloading
  // ===========================================================================

  /**
   * Preload environment textures
   */
  preloadEnvironments(environments: EnvironmentConfig[]): void {
    for (const env of environments) {
      this.getOrLoadEnvironmentTexture(env.contentId, env.path);
    }
  }

  // ===========================================================================
  // Query Methods
  // ===========================================================================

  /**
   * Check if any preview is active
   */
  hasActivePreview(): boolean {
    for (const preview of this.views.values()) {
      if (preview.view.enabled) return true;
    }
    return false;
  }

  /**
   * Check if a canvas should render as preview
   */
  shouldRenderPreview(canvas: HTMLCanvasElement): boolean {
    const preview = this.views.get(canvas);
    return preview?.view.enabled ?? false;
  }

  /**
   * Get the view for a canvas (for advanced use)
   */
  getView(canvas: HTMLCanvasElement): PreviewView | undefined {
    return this.views.get(canvas);
  }

  // ===========================================================================
  // Private Methods
  // ===========================================================================

  /**
   * Handle intersection entry with debounce for attach.
   * If unloadOnDetach is true, fully detach to free GPU memory.
   * If unloadOnDetach is false (default), only disable rendering to avoid flicker.
   */
  private handleLazyIntersection(entry: IntersectionObserverEntry): void {
    const canvas = entry.target as HTMLCanvasElement;
    const preview = this.views.get(canvas);

    if (entry.isIntersecting) {
      // Cancel any pending detach/disable
      const pendingId = this.lazyDetachTimeouts.get(canvas);
      if (pendingId !== undefined) {
        clearTimeout(pendingId);
        this.lazyDetachTimeouts.delete(canvas);
      }

      // If already attached, just re-enable
      if (preview) {
        preview.view.enabled = true;
        return;
      }

      // Cancel existing attach timeout and schedule new one
      const existingAttachId = this.lazyAttachTimeouts.get(canvas);
      if (existingAttachId !== undefined) clearTimeout(existingAttachId);
      const attachId = window.setTimeout(() => {
        this.lazyAttachTimeouts.delete(canvas);
        const config = this.registeredConfigs.get(canvas) ?? this.parseCanvasConfig(canvas);
        if (config && !this.views.has(canvas)) {
          this.attach(canvas, config);
        }
      }, LAZY_DEBOUNCE_MS);
      this.lazyAttachTimeouts.set(canvas, attachId);
    } else {
      // Cancel any pending attach
      const attachId = this.lazyAttachTimeouts.get(canvas);
      if (attachId !== undefined) {
        clearTimeout(attachId);
        this.lazyAttachTimeouts.delete(canvas);
      }

      // Cancel existing detach/disable timeout and schedule new one
      const existingId = this.lazyDetachTimeouts.get(canvas);
      if (existingId !== undefined) clearTimeout(existingId);

      const timeoutId = window.setTimeout(() => {
        this.lazyDetachTimeouts.delete(canvas);
        if (this.unloadOnDetach) {
          // Fully detach to free GPU memory
          if (this.views.has(canvas)) {
            this.detach(canvas);
          }
        } else {
          // Just disable rendering to keep content cached (no flicker on re-entry)
          const p = this.views.get(canvas);
          if (p) {
            p.view.enabled = false;
          }
        }
      }, LAZY_DEBOUNCE_MS);
      this.lazyDetachTimeouts.set(canvas, timeoutId);
    }
  }

  private parseMeshList(value: string | null): string[] | undefined {
    if (!value?.trim()) return undefined;
    return value.split(',').map(s => s.trim()).filter(Boolean);
  }

  private parseCanvasConfig(canvas: HTMLCanvasElement): AttachCanvasConfig | null {
    const contentId = canvas.getAttribute('data-preview-content');
    if (!contentId) return null;

    // Parse rotation mode from data attribute (default 'horizontal')
    const rotationAttr = canvas.getAttribute('data-preview-rotation-mode');
    const rotation = (rotationAttr as 'horizontal' | 'vertical' | 'both' | 'none') ?? 'horizontal';

    return {
      contentId,
      environmentPath: canvas.getAttribute('data-preview-env') ?? undefined,
      rotationY: parseFloat(canvas.getAttribute('data-preview-rotation') ?? '0'),
      viewerOffset: {
        x: parseFloat(canvas.getAttribute('data-preview-offset-x') ?? '0'),
        y: parseFloat(canvas.getAttribute('data-preview-offset-y') ?? '0'),
      },
      excludeMeshes: this.parseMeshList(canvas.getAttribute('data-preview-exclude-mesh')),
      includeMeshes: this.parseMeshList(canvas.getAttribute('data-preview-include-meshes')),
      rotation,
      zoom: canvas.hasAttribute('data-preview-zoom'),
      allowPageScroll: !canvas.hasAttribute('data-preview-no-page-scroll'),
    };
  }

  private async loadContent(preview: PreviewView): Promise<void> {
    try {
      // Check if instance already exists
      let instance = this.instanceManager.getInstance(preview.contentId);
      
      if (!instance) {
        // Create new instance (don't set as active - preview manages its own display)
        instance = await this.instanceManager.createInstance(preview.contentId, undefined, false);
      }
      
      // Note: 'loaded' event is emitted from the render callback when content is actually displayed
      // This ensures the UI only clears the spinner when content is visible
    } catch (error) {
      this.emitViewerEvent(
        preview.canvas,
        { type: 'error', error: error as Error },
        preview.eventHandler
      );
    }
  }

  private loadEnvironment(
    preview: PreviewView,
    contentId: string,
    path: string
  ): void {
    if (this.isEnvTexture(path)) {
      const onTextureReady = (texture: BaseTexture) => {
        preview.environmentTexture = texture;
        preview.skybox = this.createSkybox(contentId, texture);
      };

      const texture = this.getOrLoadEnvironmentTexture(contentId, path, onTextureReady);
      if (texture) {
        onTextureReady(texture);
      }
      return;
    }

    const onSkyboxReady = (texture: BaseTexture) => {
      preview.environmentTexture = texture;
      preview.skybox = this.createSkybox(contentId, texture);
    };
    const skyboxTexture = this.getOrLoadSkyboxTexture(contentId, path, onSkyboxReady);
    if (skyboxTexture) {
      onSkyboxReady(skyboxTexture);
    }
  }

  private loadIblEnvironment(
    preview: PreviewView,
    contentId: string,
    path: string
  ): void {
    if (!this.isEnvTexture(path)) return;

    const cacheKey = `ibl_${contentId}`;
    const cached = this.environmentCache.get(cacheKey);
    if (cached) {
      preview.iblEnvironmentTexture = cached;
      return;
    }

    try {
      const texture = CubeTexture.CreateFromPrefilteredData(path, this.scene);
      texture.name = cacheKey;
      texture.lodGenerationOffset = 3;
      this.environmentCache.set(cacheKey, texture);
      preview.iblEnvironmentTexture = texture;
    } catch (error) {
      console.warn(`Failed to load IBL environment: ${path}`, error);
    }
  }

  private getOrLoadEnvironmentTexture(
    contentId: string,
    path: string,
    onLoaded?: (texture: BaseTexture) => void
  ): BaseTexture | null {
    // Check cache - return immediately, caller handles callback
    const cached = this.environmentCache.get(contentId);
    if (cached) {
      return cached;
    }

    // Check if already loading - queue callback
    const existingCallbacks = this.environmentLoadingCallbacks.get(contentId);
    if (existingCallbacks) {
      if (onLoaded) existingCallbacks.push(onLoaded);
      return null;
    }

    // Start loading - store callbacks
    this.environmentLoadingCallbacks.set(contentId, onLoaded ? [onLoaded] : []);

    // .env cube textures can be created synchronously - return immediately, caller handles callback
    if (this.isEnvTexture(path)) {
      try {
        const texture = CubeTexture.CreateFromPrefilteredData(path, this.scene);
        texture.name = `env_${contentId}`;
        this.environmentCache.set(contentId, texture);
        this.environmentLoadingCallbacks.delete(contentId);
        return texture;
      } catch (error) {
        this.environmentLoadingCallbacks.delete(contentId);
        return null;
      }
    }

    return null;
  }

  private isEnvTexture(path: string): boolean {
    const normalizedPath = path.split('?')[0] ?? '';
    return normalizedPath.toLowerCase().endsWith('.env');
  }

  private getOrLoadSkyboxTexture(
    contentId: string,
    path: string,
    onLoaded?: (texture: BaseTexture) => void
  ): BaseTexture | null {
    const cached = this.skyboxTextureCache.get(contentId);
    if (cached) {
      return cached;
    }

    const existingCallbacks = this.skyboxLoadingCallbacks.get(contentId);
    if (existingCallbacks) {
      if (onLoaded) existingCallbacks.push(onLoaded);
      return null;
    }

    this.skyboxLoadingCallbacks.set(contentId, onLoaded ? [onLoaded] : []);
    this.loadSkyboxTexture(contentId, path);
    return null;
  }

  private loadSkyboxTexture(contentId: string, path: string): void {
    try {
      let isSettled = false;
      const texture = new EquiRectangularCubeTexture(
        path,
        this.scene,
        SKYBOX_EQUIRECT_SIZE,
        false,
        true,
        () => {
          if (isSettled) return;
          isSettled = true;
          texture.name = `skybox_${contentId}`;
          this.skyboxTextureCache.set(contentId, texture);
          const cbs = this.skyboxLoadingCallbacks.get(contentId) ?? [];
          for (const cb of cbs) cb(texture);
          this.skyboxLoadingCallbacks.delete(contentId);
        },
        () => {
          if (isSettled) return;
          isSettled = true;
          this.skyboxLoadingCallbacks.delete(contentId);
        },
        true
      );

      if (texture.isReady()) {
        isSettled = true;
        texture.name = `skybox_${contentId}`;
        this.skyboxTextureCache.set(contentId, texture);
        const cbs = this.skyboxLoadingCallbacks.get(contentId) ?? [];
        for (const cb of cbs) cb(texture);
        this.skyboxLoadingCallbacks.delete(contentId);
      }
    } catch {
      this.skyboxLoadingCallbacks.delete(contentId);
    }
  }

  private createSkybox(contentId: string, envTexture: BaseTexture): Mesh {
    const skybox = Mesh.CreateBox(`skybox_${contentId}`, 1000, this.scene);

    const material = new BackgroundMaterial(`skyboxMat_${contentId}`, this.scene);
    material.backFaceCulling = false;

    const skyboxTexture = envTexture.clone() as BaseTexture;
    skyboxTexture.name = `skyboxTex_${contentId}`;
    if (skyboxTexture.isCube) {
      skyboxTexture.coordinatesMode = Texture.SKYBOX_MODE;
    }

    material.reflectionTexture = skyboxTexture;
    material.reflectionBlur = envTexture instanceof CubeTexture ? SKYBOX_BLUR : 0;
    material.useRGBColor = false;
    material.enableNoise = true;

    skybox.material = material;
    skybox.infiniteDistance = true;
    skybox.renderingGroupId = 0;
    skybox.setEnabled(false);

    return skybox;
  }

  private setupExpandButton(preview: PreviewView): void {
    if (preview.expandButton) return;

    preview.expandButton = this.fullscreenController.createExpandButton(
      preview.canvas,
      () => this.expandToFullscreen(preview.canvas)
    );
    preview.expandTooltip = this.fullscreenController.createExpandTooltip(preview.canvas);
    this.fullscreenController.showExpandButton(preview);
  }

  private emitViewerEvent(
    canvas: HTMLCanvasElement,
    event: ViewerEvent,
    handler?: ViewerEventHandler | null
  ): void {
    handler?.(event);
    this.onViewerEvent?.(canvas, event);
  }

  private emitFullscreenEvent(canvas: HTMLCanvasElement, isFullscreen: boolean): void {
    this.onFullscreenChange?.({ canvas, isFullscreen });
  }

  private resetAllInstanceMeshesVisible(): void {
    const instances = this.instanceManager.getAllInstances();

    for (const instance of instances) {
      if (!instance.rootNode) continue;

      instance.rootNode.setEnabled(true);

      // Re-enable all descendants in the model hierarchy.
      for (const node of instance.rootNode.getDescendants(false)) {
        node.setEnabled(true);
      }

      for (const asset of instance.assets) {
        if (asset.type !== 'model') continue;
        const modelAsset = asset as IAssetModel;

        if (modelAsset.meshRoot) {
          modelAsset.meshRoot.setEnabled(true);
        }

        if (modelAsset.meshes) {
          for (const mesh of modelAsset.meshes) {
            mesh.setEnabled(true);
            mesh.isVisible = true;
            mesh.visibility = 1;
          }
        }
      }

      // Also reset any child meshes not present in modelAsset.meshes.
      for (const mesh of instance.rootNode.getChildMeshes(false)) {
        mesh.setEnabled(true);
        mesh.isVisible = true;
        mesh.visibility = 1;
      }
    }
  }
}
